import '../common/AggregatedLock.scss';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import MainURL from '../../../MainURL';
import { loginStateAtom, userDataAtom } from '../../../AtomStore';
import { useAggregatedInfo } from '../common/useAggregatedInfo';

// 가져온 공고 — 인수(선점) · 삭제 요청 화면 (aggregated-posting-lock-design.md 5장)
// /recruit/claim/:kind/:id            인수 요청 (?mode=delete 이면 삭제 요청, ?m= 인수 유도 메일 클릭)
// /recruit/claim/verify?token=        인증 메일의 링크 → 인수 완료
// /recruit/claim/delete-confirm?token= 삭제 확인 메일의 링크 → 즉시 비노출

const n = (v: number) => Number(v || 0).toLocaleString('ko-KR');
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const detailPath = (kind: string, id: number | string) => `/recruit/${kind}/${id}`;
const APPLY_KINDS = ['minister', 'overseas'];

type Sent = { kind: 'sent'; maskedEmail: string } | { kind: 'review' } | { kind: 'already' };

export function AggregatedClaimPage() {
  const { kind = '', id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);
  const mode = search.get('mode') === 'delete' ? 'delete' : 'claim';
  const mailId = search.get('m');
  const isLogin = useAtomValue(loginStateAtom);
  const userData = useAtomValue(userDataAtom);
  const loggedIn = Boolean(isLogin && userData.userAccount);
  const info = useAggregatedInfo(kind, id, false);

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<Sent | null>(null);

  // 인수 유도 메일 클릭 기록 (발송 → 클릭 → 선점 전환 측정)
  const clickLogged = useRef(false);
  useEffect(() => {
    if (!mailId || clickLogged.current) return;
    clickLogged.current = true;
    axios.post(`${MainURL}/aggregated/mail-click`, { m: mailId }).catch(() => {});
  }, [mailId]);

  const submit = async () => {
    if (busy) return;
    if (!isEmail(email)) {
      setError('교회 담당자 이메일을 정확히 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const url = mode === 'delete' ? `${MainURL}/aggregated/delete/${kind}/${id}` : `${MainURL}/aggregated/claim/${kind}/${id}`;
      const res = await axios.post(url, { email: email.trim(), m: mailId || undefined });
      if (res.data.already) setSent({ kind: 'already' });
      else if (res.data.review) setSent({ kind: 'review' });
      else setSent({ kind: 'sent', maskedEmail: res.data.maskedEmail });
    } catch (err: any) {
      setError(err.response?.data?.error || '요청을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };

  if (!info) {
    return <div className="agg-page"><p className="agg-page__lead">불러오는 중...</p></div>;
  }

  if (!info.managed) {
    return (
      <div className="agg-page">
        <h3>인수할 수 있는 공고가 아닙니다</h3>
        <p className="agg-page__lead">사역자모아에 직접 등록된 공고이거나 인수 대상이 아닌 공고입니다. 수정·삭제는 고객문의로 알려 주세요.</p>
        <div className="agg-page__actions"><Link className="agg-btn" to={detailPath(kind, id)}>공고로 돌아가기</Link></div>
      </div>
    );
  }

  if (mode === 'claim' && info.claimStatus === 'CLAIMED') {
    return (
      <div className="agg-page">
        <h3>{info.isOwner ? '이미 인수한 공고입니다' : '이미 인수된 공고입니다'}</h3>
        <p className="agg-page__lead">{info.church} · {info.title}</p>
        <div className="agg-page__actions">
          {info.isOwner && APPLY_KINDS.includes(kind) && (
            <Link className="agg-btn agg-btn--primary" to={`/mypage/applicantmanage?id=${id}&tableType=${kind}`}>지원자 관리</Link>
          )}
          <Link className="agg-btn" to={detailPath(kind, id)}>공고 보기</Link>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="agg-page">
        {sent.kind === 'sent' && (
          <>
            <h3>메일을 보냈습니다: {sent.maskedEmail}</h3>
            <p className="agg-page__lead">
              받은편지함의 링크를 눌러 {mode === 'delete' ? '삭제를' : '인수를'} 완료해 주세요. (유효 24시간)
              {mode === 'claim' && ' 메일을 다른 기기에서 열어도 됩니다.'}
            </p>
          </>
        )}
        {sent.kind === 'review' && (
          <>
            <h3>운영자가 확인한 뒤 처리합니다</h3>
            <p className="agg-page__lead">
              입력하신 주소가 게시판에 등록된 주소와 달라 운영자가 전화로 확인한 뒤 {mode === 'delete' ? '삭제합니다' : '인수를 승인합니다'}.
              보통 1영업일 안에 처리됩니다.
            </p>
          </>
        )}
        {sent.kind === 'already' && <h3>이미 처리된 공고입니다</h3>}
        <div className="agg-page__actions"><Link className="agg-btn" to={detailPath(kind, id)}>공고로 돌아가기</Link></div>
      </div>
    );
  }

  if (mode === 'delete') {
    return (
      <div className="agg-page">
        <h3>공고 삭제 요청</h3>
        <p className="agg-page__lead">{info.church} · {info.title}</p>
        <div className="agg-page__card">
          <p className="agg-page__hint">
            담당자 이메일로 확인 메일을 보내 드립니다. 메일의 버튼을 누르면 공고가 즉시 내려가고, 같은 공고는 다시 가져오지 않습니다.
          </p>
          <label className="agg-page__field">
            <span>교회 담당자 이메일</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" placeholder="pastor@church.kr" />
          </label>
          {info.knownEmailHint && <p className="agg-page__hint">게시판에 등록된 주소({info.knownEmailHint})로 받으시면 바로 처리됩니다.</p>}
          {error && <p className="agg-page__error">{error}</p>}
          <div className="agg-page__actions">
            <button type="button" className="agg-btn" onClick={() => navigate(detailPath(kind, id))}>취소</button>
            <button type="button" className="agg-btn agg-btn--primary" onClick={submit} disabled={busy}>
              {busy ? '보내는 중...' : '확인 메일 받기'}
            </button>
          </div>
          <p className="agg-page__hint" style={{ marginTop: 16, marginBottom: 0 }}>
            공고를 내리는 대신 직접 관리하실 수도 있습니다 — <Link to={`/recruit/claim/${kind}/${id}`}>인수하면</Link> 공고를 수정·마감하고 지원자를 직접 볼 수 있습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="agg-page">
      <h3>공고 인수(선점)</h3>
      <p className="agg-page__lead">
        {info.church} · {info.title}
        {info.sourceBoard ? ` — ${info.sourceBoard} 게시판에서 옮겨 온 공고입니다.` : ''}
      </p>
      <div className="agg-page__card">
        <div className="agg-stats" style={{ marginTop: 0, marginBottom: 16 }}>
          <span>지금까지 조회 <b>{n(info.viewCount)}</b></span>
          {APPLY_KINDS.includes(kind) && <span>관심 표시 <b>{n(info.interestCount)}</b></span>}
        </div>
        <ul className="agg-page__list">
          <li>공고를 수정하고 마감할 수 있습니다</li>
          {APPLY_KINDS.includes(kind) && <li>지원자 목록과 이력서를 열람할 수 있습니다</li>}
          <li>30일 자동 비노출이 해제됩니다{info.unpublished === 'EXPIRED' ? ' (내려간 공고도 다시 게시됩니다)' : ''}</li>
          <li>비용은 없습니다</li>
        </ul>

        {!loggedIn ? (
          <>
            <p className="agg-page__hint">인수하려면 교회 계정으로 로그인해 주세요. 로그인한 뒤 이 화면으로 다시 들어오면 됩니다.</p>
            <div className="agg-page__actions">
              <button type="button" className="agg-btn" onClick={() => navigate(detailPath(kind, id))}>나중에</button>
              <Link className="agg-btn agg-btn--primary" to="/login">로그인하기</Link>
            </div>
          </>
        ) : (
          <>
            <label className="agg-page__field">
              <span>교회 담당자 이메일</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" placeholder="pastor@church.kr" />
            </label>
            <p className="agg-page__hint">
              ⓘ 게시판에 등록된 주소{info.knownEmailHint ? `(${info.knownEmailHint})` : ''}와 일치하거나 같은 교회 도메인이면 인증 메일로 바로 인증됩니다.
              다르면 운영자가 전화로 확인한 뒤 승인합니다.
            </p>
            {error && <p className="agg-page__error">{error}</p>}
            <div className="agg-page__actions">
              <button type="button" className="agg-btn" onClick={() => navigate(detailPath(kind, id))}>나중에</button>
              <button type="button" className="agg-btn agg-btn--primary" onClick={submit} disabled={busy || !info.claimable}>
                {busy ? '보내는 중...' : '인증 메일 받기'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface VerifyResult {
  kind: string;
  id: number;
  church: string;
  title: string;
  delivered: number;
  expiresAt: string | null;
  viewCount: number;
  interestCount: number;
  already?: boolean;
}

/** 인증 메일의 1회용 링크 → 인수 완료 (5.4) */
export function AggregatedClaimVerifyPage() {
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState('');
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    axios.post(`${MainURL}/aggregated/claim/verify`, { token })
      .then(res => setResult(res.data as VerifyResult))
      .catch(err => setError(err.response?.data?.error || '인수를 완료하지 못했습니다.'));
  }, [token]);

  if (error) {
    return (
      <div className="agg-page">
        <h3>인수를 완료하지 못했습니다</h3>
        <p className="agg-page__lead">{error}</p>
      </div>
    );
  }
  if (!result) return <div className="agg-page"><p className="agg-page__lead">인수를 완료하는 중...</p></div>;

  const canManage = APPLY_KINDS.includes(result.kind);
  return (
    <div className="agg-page">
      <div className="agg-page__card agg-page__done">
        <h3>{result.already ? '이미 인수가 완료되었습니다' : '인수 완료 🎉'}</h3>
        {!result.already && (
          <>
            <p className="agg-page__lead">
              자동 비노출이 해제되었습니다{result.expiresAt ? ` (만료 ${result.expiresAt.slice(0, 10)})` : ''}.
              {result.delivered > 0 && ` 보류 중이던 관심 표시 ${result.delivered}건이 전달되었습니다.`}
            </p>
            <p className="agg-page__lead">{result.church} · {result.title}</p>
            <div className="agg-stats">
              <span>조회 <b>{n(result.viewCount)}</b></span>
              {canManage && <span>관심 표시 <b>{n(result.interestCount)}</b></span>}
            </div>
          </>
        )}
        <div className="agg-page__actions" style={{ justifyContent: 'center', marginTop: 20 }}>
          {canManage && (
            <Link className="agg-btn agg-btn--primary" to={`/mypage/applicantmanage?id=${result.id}&tableType=${result.kind}`}>지원자 관리</Link>
          )}
          <Link className="agg-btn" to={detailPath(result.kind, result.id)}>공고 보기</Link>
          <Link className="agg-btn" to="/mypage">마이페이지 · 공고 수정</Link>
        </div>
        <p className="agg-page__hint" style={{ marginTop: 16, marginBottom: 0 }}>인수한 계정으로 로그인하면 마이페이지에서 공고를 수정·마감할 수 있습니다.</p>
      </div>
    </div>
  );
}

/** 삭제 확인 메일의 링크 → 즉시 비노출 (5.5) */
export function AggregatedDeleteConfirmPage() {
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading');
  const [error, setError] = useState('');
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    axios.post(`${MainURL}/aggregated/delete/confirm`, { token })
      .then(() => setState('done'))
      .catch(err => {
        setError(err.response?.data?.error || '삭제를 완료하지 못했습니다.');
        setState('error');
      });
  }, [token]);

  if (state === 'loading') return <div className="agg-page"><p className="agg-page__lead">처리하는 중...</p></div>;
  if (state === 'error') {
    return (
      <div className="agg-page">
        <h3>삭제를 완료하지 못했습니다</h3>
        <p className="agg-page__lead">{error}</p>
      </div>
    );
  }
  return (
    <div className="agg-page">
      <h3>공고를 내렸습니다</h3>
      <p className="agg-page__lead">요청하신 공고가 사이트에서 내려갔고, 같은 공고는 다시 가져오지 않습니다. 불편을 드려 죄송합니다.</p>
      <p className="agg-page__hint">
        다음 청빙 때는 사역자모아에 직접 등록하시면 공고를 수정·마감하고 지원자를 직접 관리하실 수 있습니다.
      </p>
      <div className="agg-page__actions" style={{ justifyContent: 'flex-start' }}>
        <Link className="agg-btn" to="/recruit">사역자모아 둘러보기</Link>
      </div>
    </div>
  );
}
